import type { CapturedRequest } from './api.js';

/**
 * Rebuilds a captured request as a runnable `curl` command.
 *
 * The promise this makes is that running the output produces an equivalent
 * request — so the reproduction has to keep the verb, the full URL with its
 * query, the headers the sender chose, and the body byte-for-byte. What it must
 * *not* keep is the framing the transport owns: `host`, `content-length` and
 * the hop-by-hop headers are recomputed by curl, and echoing them back would
 * produce a command that either fails or lies about what it sends.
 */

/** Headers curl or the transport sets itself. Replaying them breaks the request. */
const TRANSPORT_HEADERS: ReadonlySet<string> = new Set([
  'host',
  'content-length',
  'connection',
  'keep-alive',
  'proxy-authorization',
  'proxy-connection',
  'te',
  'trailer',
  'transfer-encoding',
  'upgrade',
]);

/**
 * Added by the reverse proxy in front of us, not by the caller. Replaying them
 * would put the original client's IP in a header the new request has no
 * business asserting.
 */
const PROXY_HEADERS = /^(x-forwarded-|x-real-ip$|cf-|forwarded$)/;

/**
 * POSIX single-quoting: everything inside `'…'` is literal, and the only
 * character that needs care is the quote itself, closed and re-opened around an
 * escaped one. This is what keeps a payload full of quotes and backslashes from
 * turning into shell syntax.
 */
export function shellQuote(value: string): string {
  return `'${value.replaceAll("'", `'\\''`)}'`;
}

function headerLines(headers: Record<string, string | string[]>): string[] {
  const lines: string[] = [];

  for (const [rawName, rawValue] of Object.entries(headers)) {
    const name = rawName.toLowerCase();
    if (TRANSPORT_HEADERS.has(name) || PROXY_HEADERS.test(name)) continue;

    for (const value of Array.isArray(rawValue) ? rawValue : [rawValue]) {
      lines.push(`-H ${shellQuote(`${name}: ${value}`)}`);
    }
  }

  return lines.sort();
}

/** The capture URL this request would have to be sent to, query included. */
export function replayUrl(endpointUrl: string, request: CapturedRequest): string {
  const base = endpointUrl.replace(/\/+$/, '');
  const path = request.path === '/' ? '' : request.path;

  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(request.query ?? {})) {
    for (const item of Array.isArray(value) ? value : [value]) {
      query.append(key, String(item));
    }
  }

  const suffix = query.size > 0 ? `?${query}` : '';
  return `${base}${path}${suffix}`;
}

export interface CurlOptions {
  /** The endpoint's public capture URL, e.g. `https://mock.montlab.dev/e/7fk2mqxa`. */
  endpointUrl: string;
}

export function toCurl(request: CapturedRequest, options: CurlOptions): string {
  const url = replayUrl(options.endpointUrl, request);
  const parts = [`curl -X ${request.method} ${shellQuote(url)}`, ...headerLines(request.headers)];

  const notes: string[] = [];

  if (request.bodyTruncated) {
    // Saying so matters: the command is honest about reproducing the capture,
    // not the original request, and those differ once past 256 KB.
    notes.push(
      `# El cuerpo se guardó truncado (${request.bodySize} bytes recibidos); esto reproduce solo lo capturado.`,
    );
  }

  if (request.body === null || request.body.length === 0) {
    return [...notes, parts.join(' \\\n  ')].join('\n');
  }

  if (request.bodyEncoding === 'base64') {
    // Binary cannot survive a shell-quoted literal, so it travels as base64 and
    // is decoded into curl's stdin. `--data-binary @-` keeps the bytes exact.
    parts.push('--data-binary @-');
    return [
      ...notes,
      `printf '%s' ${shellQuote(request.body)} | base64 -d | \\`,
      `  ${parts.join(' \\\n  ')}`,
    ].join('\n');
  }

  // `--data-raw`, never `-d`: `-d` strips newlines and would quietly change a
  // multi-line payload, which is exactly the kind of difference people are here
  // to chase down.
  parts.push(`--data-raw ${shellQuote(request.body)}`);
  return [...notes, parts.join(' \\\n  ')].join('\n');
}

/** The paste-ready example shown while an endpoint has captured nothing yet. */
export function exampleCurl(endpointUrl: string): string {
  return [
    `curl -X POST ${shellQuote(`${endpointUrl.replace(/\/+$/, '')}/hola`)} \\`,
    `  -H 'content-type: application/json' \\`,
    `  --data-raw '{"hola":"mundo"}'`,
  ].join('\n');
}
